import React, { useState } from 'react';
import GVTextField from 'components/lib/GVTextField/GVTextField';
import { VerifyDialog } from 'components/common';
import { DIALOG_ICON_TYPES } from 'components/common/VerifyDialog/constants';
import { useFlags } from 'launchdarkly-react-client-sdk';

interface UrlModalProps {
  open: boolean;
  handleClose: () => void;
  handleSubmit: (url: string) => void;
}

const UploadFromUrlModal: React.FC<UrlModalProps> = ({ open, handleClose, handleSubmit }) => {
  const [url, setUrl] = useState<string>('');
  const [error, setError] = useState<string>('');
  const flags = useFlags();

  /**
   * When the value of the form changes it validates input based on the `allowUploadGvaultId` flag:
   * allows URLs and numbers if ON, only URLs if OFF.
   * Sets an error message for invalid inputs or clears it for valid ones. Updates the input value using `setUrl`.
   */
  const handleUrlChange = (value: string | null) => {
    const nextValue = value ?? '';
    setUrl(nextValue);

    const isNumber = (val: string): boolean => /^\d+$/.test(val);
    const isValidUrl = (val: string): boolean => /^(https?:\/\/[^\s/$.?#].[^\s]*)$/.test(val);

    if (flags.allowUploadGvaultId) {
      if (isValidUrl(nextValue)) {
        setError('');
        return;
      }

      if (isNumber(nextValue)) {
        setError('');
        return;
      }

      setError('Invalid input. Must be a valid URL or a number.');
      return;
    }

    if (!isValidUrl(nextValue)) {
      setError('Invalid URL. Must start with "https://" or "http://".');
      return;
    }

    setError('');
  };

  const onSubmit = () => {
    if (!error && url) {
      handleSubmit(url);
      handleClose(); // Close the modal after submission
    }
  };

  return (
    <VerifyDialog
      title="Open from URL"
      subTitle="Enter a URL from the web to be converted and loaded as a PDF."
      callToActionText="Submit"
      handleCallToActionClick={onSubmit}
      iconType={DIALOG_ICON_TYPES.HTTP}
      isDivider
      open={open}
      handleCloseModal={handleClose}
      children={
        <GVTextField
          label={flags.allowUploadGvaultId ? 'Enter a valid URL or GVault ID' : 'Enter a valid URL'}
          value={url}
          onValueChange={handleUrlChange}
          hasError={!!error}
          message={error || ''}
        />
      }
    />
  );
};

export default UploadFromUrlModal;
